/**
 * ============================================
 * MULTIPLAYER CHART PIECES
 * ============================================
 *
 * Shared presentational components for the Multiplayer tab. Conventions
 * (MULTIPLAYER_TAB_PLAN.md section 8.0): graph id chip, n on every card,
 * badges for partial data, ATH / Now markers on time charts, "No data"
 * instead of zeros.
 */

import React, { createContext, useContext } from 'react';
import { Info } from 'lucide-react';
import { CardCountryChips, CardCountrySelect } from '@/components/common/CardCountryFilter';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
} from 'recharts';
import { useTheme } from '@/context/ThemeContext';
import { binLabelOf, type HistBin } from './mpData';
import { markerLabel } from '@/components/common/ChartMarkers';

export const PALETTE = [
  '#3b82f6', '#f97316', '#10b981', '#eab308', '#ec4899', '#6366f1',
  '#22c55e', '#ef4444', '#0ea5e9', '#a855f7', '#14b8a6', '#64748b',
];

export const END_REASON_LABELS = ['Finished (portal)', 'Quit to menu', 'Opponent left', 'Disconnected'];
export const END_REASON_SHORT = ['Portal', 'Quit', 'Opp. left', 'Disc.'];
export const END_REASON_COLORS = ['#10b981', '#f97316', '#3b82f6', '#ef4444'];
export const MATCH_TYPE_LABELS = ['Unknown', 'Host', 'Join', 'Matchmaking'];
export const INPUT_LABELS = ['Unknown', 'Keyboard', 'Xbox', 'DualSense', 'Touch'];
export const SOURCE_LABELS = ['Both', 'S3 only', 'DynamoDB only'];
export const SOURCE_COLORS = ['#10b981', '#3b82f6', '#f97316'];

export interface ChartTheme {
  isDark: boolean;
  grid: string;
  text: string;
  tooltip: React.CSSProperties;
}

export function useChartTheme(): ChartTheme {
  const { isDarkMode } = useTheme();
  return {
    isDark: isDarkMode,
    grid: isDarkMode ? '#374151' : '#e5e7eb',
    text: isDarkMode ? '#9ca3af' : '#6b7280',
    tooltip: {
      backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
      border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
      borderRadius: 8,
      fontSize: 12,
      color: isDarkMode ? '#f3f4f6' : '#111827',
    },
  };
}

/** X axis props for period charts (rotated labels, like the Sony/DOMO tab). */
export function periodAxis(t: ChartTheme) {
  return {
    dataKey: 'label',
    tick: { fontSize: 10, fill: t.text },
    stroke: t.grid,
    angle: -35,
    textAnchor: 'end' as const,
    height: 55,
    interval: 'preserveStartEnd' as const,
  };
}

export function ThemedCard({ children, isDark, className = '' }: { children: React.ReactNode; isDark: boolean; className?: string }) {
  return (
    <div className={`rounded-xl border ${isDark ? 'bg-gray-800/50 border-gray-700/50' : 'bg-white border-gray-300 shadow-sm'} ${className}`}>
      {children}
    </div>
  );
}

const BADGES = {
  v221: { text: 'v2.21 only', title: 'Only v2.21 recorded this; newer versions do not.' },
  lowerBound: { text: 'lower bound', title: 'Players are identified through DynamoDB only; S3-only reports have no device id.' },
  complete: { text: 'complete matches', title: 'Only matches where both players’ reports exist.' },
  fixedWindow: { text: 'date filter not applied', title: 'Fixed historical window (25 Jun – 17 Jul 2026).' },
  noRegion: { text: 'region & map filters not applied', title: 'Menu events have no server region or map.' },
  noFilters: { text: 'filters not applied', title: 'Level events are pre-aggregated; platform, version, region and map filters do not apply.' },
  ownWindow: { text: 'Day/Week/Month window', title: 'The window ends at the last complete day (or the end of the date range) and stays inside the date range.' },
} as const;

export type BadgeKind = keyof typeof BADGES;

/** A chart's own window. 'range' follows the date range; Day / Week / Month end at the
 *  last complete day and, like All Time, ignore the date range. */
export type ChartWindow = 'range' | 'day' | 'week' | 'month' | 'all';
export const CHART_WINDOW_OPTIONS: { value: ChartWindow; label: string }[] = [
  { value: 'range', label: 'Range' },
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'all', label: 'All Time' },
];
/** Provided around a ChartCard to give it a window switch in its header. */
export const ChartWindowContext = createContext<{ value: ChartWindow; onChange: (w: ChartWindow) => void; label?: string } | null>(null);

// Hidden, not removed: drop a kind from this list to show its tag again.
const HIDDEN_BADGES: BadgeKind[] = ['lowerBound', 'noRegion'];

export function Badge({ kind, isDark }: { kind: BadgeKind; isDark: boolean }) {
  const b = BADGES[kind];
  return (
    <span title={b.title}
      className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${
        kind === 'v221'
          ? isDark ? 'bg-amber-900/40 text-amber-300 border border-amber-800/60' : 'bg-amber-50 text-amber-700 border border-amber-200'
          : isDark ? 'bg-gray-700 text-gray-300 border border-gray-600' : 'bg-gray-100 text-gray-600 border border-gray-200'
      }`}>
      {b.text}
    </span>
  );
}

export function ChartCard({ id, title, badges = [], n, right, height = 230, footer, className = '', children }: {
  id: string;
  title: string;
  badges?: BadgeKind[];
  n?: number;
  right?: React.ReactNode;
  height?: number;
  /** a plain-text note shows as an info icon (hover) to save vertical space */
  footer?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}) {
  const { isDark } = useChartTheme();
  const win = useContext(ChartWindowContext);
  const note = typeof footer === 'string' ? footer : null;
  return (
    <ThemedCard isDark={isDark} className={`min-w-0 ${className}`}>
      <div className={`px-3 py-2 border-b flex items-center justify-between gap-x-2 gap-y-1 flex-wrap ${isDark ? 'border-gray-700/50' : 'border-gray-200'}`}>
        <h3 className={`text-sm font-semibold flex items-center gap-x-2 gap-y-1 flex-wrap ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
          <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono ${isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>{id}</span>
          {title}
          {note && (
            <span title={note} aria-label={note} className={`cursor-help ${isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}>
              <Info className="h-3.5 w-3.5" />
            </span>
          )}
          {badges.filter(b => !HIDDEN_BADGES.includes(b)).map(b => <Badge key={b} kind={b} isDark={isDark} />)}
          {n !== undefined && (
            <span className={`text-[11px] font-normal ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>n = {n.toLocaleString()}</span>
          )}
          {win?.label && (
            <span className={`text-[11px] font-normal ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{win.label}</span>
          )}
        </h3>
        <div className="flex items-center gap-1.5 flex-wrap">
          <CardCountrySelect />
          {win && <Segmented size="sm" value={win.value} options={CHART_WINDOW_OPTIONS} onChange={win.onChange} />}
          {right}
        </div>
        <div className="basis-full empty:hidden"><CardCountryChips /></div>
      </div>
      <div className="p-3" style={{ height }}>{children}</div>
      {footer && !note && <div className={`px-3 pb-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{footer}</div>}
    </ThemedCard>
  );
}

export function EmptyChart({ message = 'No data for this range' }: { message?: string }) {
  const { isDark } = useChartTheme();
  return (
    <div className={`h-full flex items-center justify-center text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{message}</div>
  );
}

/** ATH (red) + Now (green) reference lines. Call inline inside a chart. */
export function athNowLines(values: number[], format: (v: number) => string = v => v.toLocaleString(), yAxisId?: string) {
  if (!values.length) return null;
  const ath = Math.max(...values);
  const cur = values[values.length - 1];
  const axis = yAxisId ? { yAxisId } : {};
  return (
    <>
      {ath > 0 && (
        <ReferenceLine {...axis} y={ath} stroke="#ef4444" strokeDasharray="4 3" strokeWidth={1.5}
          label={markerLabel(`ATH ${format(ath)}`, '#ef4444', 'above')} />
      )}
      {cur > 0 && cur !== ath && (
        <ReferenceLine {...axis} y={cur} stroke="#22c55e" strokeDasharray="4 3" strokeWidth={1.5}
          label={markerLabel(`Now ${format(cur)}`, '#22c55e', 'below')} />
      )}
    </>
  );
}

export function Donut({ data }: { data: { name: string; value: number; color: string }[] }) {
  const t = useChartTheme();
  const shown = data.filter(d => d.value > 0);
  const total = shown.reduce((s, d) => s + d.value, 0);
  if (!total) return <EmptyChart />;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie data={shown} dataKey="value" nameKey="name" innerRadius="55%" outerRadius="80%" paddingAngle={1} stroke="none">
          {shown.map(d => <Cell key={d.name} fill={d.color} />)}
        </Pie>
        <Tooltip contentStyle={t.tooltip}
          formatter={(v: number, name: string) => [`${v.toLocaleString()} (${((v / total) * 100).toFixed(1)}%)`, name]} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
      </PieChart>
    </ResponsiveContainer>
  );
}

export function HistogramChart({ bins, color = PALETTE[0], medianValue, medianLabel, valueLabel = 'Count' }: {
  bins: HistBin[];
  color?: string;
  medianValue?: number;
  medianLabel?: string;
  valueLabel?: string;
}) {
  const t = useChartTheme();
  if (!bins.some(b => b.count > 0)) return <EmptyChart />;
  const medianBin = medianValue !== undefined ? binLabelOf(bins, medianValue) : undefined;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={bins}>
        <CartesianGrid strokeDasharray="3 3" stroke={t.grid} />
        <XAxis dataKey="bin" tick={{ fontSize: 9, fill: t.text }} stroke={t.grid} angle={-35} textAnchor="end" height={55} interval="preserveStartEnd" />
        <YAxis tick={{ fontSize: 10, fill: t.text }} stroke={t.grid} allowDecimals={false} />
        <Tooltip contentStyle={t.tooltip} formatter={(v: number) => [v.toLocaleString(), valueLabel]} />
        {medianBin && (
          <ReferenceLine x={medianBin} stroke="#eab308" strokeDasharray="4 3" strokeWidth={1.5}
            label={{ value: medianLabel ?? `median ${medianValue}`, position: 'top', fill: '#eab308', fontSize: 10, fontWeight: 600 }} />
        )}
        <Line type="monotone" dataKey="count" stroke={color} strokeWidth={2} dot={{ r: 2 }} />
      </LineChart>
    </ResponsiveContainer>
  );
}

export function StatTile({ label, value, sub, tone = 'blue' }: {
  label: string;
  value: string;
  sub?: string;
  tone?: 'blue' | 'green' | 'orange' | 'red' | 'purple' | 'gray';
}) {
  const { isDark } = useChartTheme();
  const border = { blue: 'border-l-blue-500', green: 'border-l-green-500', orange: 'border-l-orange-500', red: 'border-l-red-500', purple: 'border-l-purple-500', gray: 'border-l-gray-400' }[tone];
  return (
    <ThemedCard isDark={isDark} className={`border-l-4 ${border}`}>
      <div className="p-3">
        <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{label}</div>
        <div className={`text-xl font-bold mt-0.5 ${isDark ? 'text-white' : 'text-gray-900'}`}>{value}</div>
        {sub && <div className={`text-[11px] mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{sub}</div>}
      </div>
    </ThemedCard>
  );
}

/** Small segmented control, styled like TimeScaleToggle. */
export function Segmented<T extends string>({ value, options, onChange, size = 'md' }: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
  size?: 'sm' | 'md';
}) {
  const { isDark } = useChartTheme();
  return (
    <div className={`flex rounded-lg p-0.5 ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
      {options.map(o => (
        <button key={o.value} onClick={() => onChange(o.value)}
          className={`${size === 'sm' ? 'px-1.5 py-0.5 text-[11px]' : 'px-2 py-1 text-xs'} whitespace-nowrap font-medium rounded-md transition-colors ${
            value === o.value
              ? isDark ? 'bg-blue-600 text-white' : 'bg-white text-blue-600 shadow-sm'
              : isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'
          }`}>{o.label}</button>
      ))}
    </div>
  );
}

export function SectionTitle({ title, subtitle }: { title: string; subtitle?: string }) {
  const { isDark } = useChartTheme();
  return (
    <div className="pt-2">
      <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{title}</h2>
      {subtitle && <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{subtitle}</p>}
    </div>
  );
}
